'use client'

import { useFormikContext } from 'formik'
import { FormInput } from '@/components/form-inputs/form-input'
import { FormSelect } from '@/components/form-inputs/form-select'
import { FormSwitch } from '@/components/form-inputs/form-switch'
import { isDefaultFreePlanName } from '@/lib/billing/default-free-plan'
import type { BillingPlan } from '@/lib/billing/types'
import { getFIELD_LABELS, getNUMERIC_FIELDS, type SectionId } from './catalog'
import type { PlanEditorValues } from './model'
import { Benefits, SectionCard as Card, FieldGrid as Grid, Notice, NumberControl } from './fields'
import { ProviderSettings } from './provider-settings'
import { useTranslations } from "next-intl"



interface EditorSectionProps {
  section: SectionId
  planId?: string
  onSyncingChange: (syncing: boolean) => void
  onSynced: (plan: BillingPlan) => void
}

export function EditorSection({ section, planId, onSyncingChange, onSynced }: EditorSectionProps) {
  switch (section) {
    case 'overview': return <Overview creating={!planId} />
    case 'pricing': return <Pricing planId={planId} onSyncingChange={onSyncingChange} onSynced={onSynced} />
    case 'credits': return <Credits />
    case 'voice': return <Voice />
    case 'resources': return <Resources />
    case 'shopify': return <Shopify />
    case 'lifecycle': return <Lifecycle />
  }
}

function Overview({ creating }: { creating: boolean }) {
  const L = getFIELD_LABELS(useTranslations("PlanCatalog"))
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))

  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  const { values } = useFormikContext<PlanEditorValues>()
  const system = isDefaultFreePlanName(values.name)
  return <>
    <Card title={tUi("planIdentity")}>
      {system ? <Notice><span key={tUi("systemFreePlansMustHaveAZeroPrice")}>{tUi("systemFreePlansMustHaveAZeroPrice")}</span></Notice> : null}
      <Grid>
        <FormInput name="display_name" label={L.display_name} placeholder={tUi("eGGrowth")} description={tUi("shownToCustomersOnPricingAndBillingPages")} />
        <FormInput name="name" label={L.name} placeholder={tUi("eGGrowthStripe")} disabled={!creating} description={tUi("uniqueIdentifierCannotBeRenamedAfterCreation")} />
      </Grid>
      <Grid>
        <FormSelect name="status" label={L.status} disabled={values.status === 'archived'} options={visibilityOptions(system, values.status, tUi)} description={tUi("internalPlansAreAssignedByAnAdministrator")} />
        <NumberControl field={F.order} />
      </Grid>
      {values.status === 'archived' ? <Notice><span key={tUi("restoreThisPlanFromThePlanListBefore")}>{tUi("restoreThisPlanFromThePlanListBefore")}</span></Notice> : null}
    </Card>
    <Card title={L.benefits}><Benefits /></Card>
  </>
}

function Pricing({ planId, onSyncingChange, onSynced }: Omit<EditorSectionProps, 'section'>) {
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))
  const L = getFIELD_LABELS(useTranslations("PlanCatalog"))


  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  const { values } = useFormikContext<PlanEditorValues>()
  const fixedSystem = Boolean(planId) && isDefaultFreePlanName(values.name)
  return <>
    <Card title={tUi("subscriptionPrice")}>
      <Grid>
        <NumberControl field={F.price} disabled={fixedSystem} />
        <FormSelect name="billing_channel" label={L.billing_channel} disabled={fixedSystem} options={[{ value: 'stripe', label: "Stripe" }, { value: 'shopify', label: "Shopify" }]} description={tUi("determinesThePaymentProviderAndWhichBillingChannel")} />
      </Grid>
    </Card>
    <Card
      title={values.billing_channel === 'shopify' ? tUi("shopifyBilling") : tUi("stripeBilling")}
      className="border-violet-300 bg-violet-50 dark:border-violet-800 dark:bg-violet-950/30"
    >
      <ProviderSettings planId={planId} onSyncingChange={onSyncingChange} onSynced={onSynced} />
    </Card>
    <Card title={tUi("textUsagePricing")}><Grid><NumberControl field={F.textMultiplier} /><NumberControl field={F.textMinimum} /><NumberControl field={F.textRounding} /></Grid></Card>
    <Card title={tUi("voiceUsagePricing")}><Grid><NumberControl field={F.voiceMultiplier} /><NumberControl field={F.voiceMinimum} /><NumberControl field={F.voiceRounding} /></Grid></Card>
    <Card title={tUi("knowledgeEmbeddingPricing")}>
      <Grid><NumberControl field={F.embeddingMinimum} /><NumberControl field={F.embeddingRounding} /></Grid>
      <Notice><span key={tUi("usesTheTextCostMultiplierRoundingChangesCharges")}>{tUi("usesTheTextCostMultiplierRoundingChangesCharges")}</span></Notice>
    </Card>
  </>
}

function Credits() {
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))
  const L = getFIELD_LABELS(useTranslations("PlanCatalog"))

  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  return <>
    <Card title={tUi("creditGrants")}>
      <NumberControl field={F.initial} />
      <Grid><NumberControl field={F.recurring} /><NumberControl field={F.payments} /></Grid>
      <FormSwitch name="plan_credits_expire" label={L.plan_credits_expire} description={tUi("creditsFromEachPaymentAreValidUntilThe")} />
      <Notice><span key={tUi("startingCreditsAndPaymentCreditsAreSeparateGrants")}>{tUi("startingCreditsAndPaymentCreditsAreSeparateGrants")}</span></Notice>
    </Card>
    <Card title={tUi("purchasedTopUps")}><Grid><NumberControl field={F.topup} /><NumberControl field={F.bonus} /></Grid></Card>
    <Card title={tUi("spendingLimits")}>
      <Grid><NumberControl field={F.daily} /><NumberControl field={F.monthly} /></Grid>
      <Notice><span key={tUi("theseCapsLimitUsageTheyDoNotSet")}>{tUi("theseCapsLimitUsageTheyDoNotSet")}</span></Notice>
    </Card>
  </>
}

function Voice() {
  const L = getFIELD_LABELS(useTranslations("PlanCatalog"))
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))

  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  const { values, errors, setValues } = useFormikContext<PlanEditorValues>()
  // Stays visible while a field needs fixing.
  const showTrial = values.voice_trial_enabled || Boolean(errors.voice_trial_duration_hours || errors.voice_trial_budget_microusd)
  function changeVoiceAccess(field: 'voice_included' | 'voice_trial_enabled', enabled: boolean) {
    const other = field === 'voice_included' ? 'voice_trial_enabled' : 'voice_included'
    void setValues(current => ({ ...current, [field]: enabled, [other]: enabled ? false : current[other] }))
  }
  return <>
    <Card title={tUi("voiceAccess")}>
      <FormSwitch name="voice_included" label={L.voice_included} description={tUi("permanentVoiceAccessEnablingThisTurnsOffVoice")} onValueChange={enabled => changeVoiceAccess('voice_included', enabled)} />
      <FormSwitch name="voice_trial_enabled" label={L.voice_trial_enabled} description={tUi("trialAccessInsteadOfPermanentAccessEnablingThis")} onValueChange={enabled => changeVoiceAccess('voice_trial_enabled', enabled)} />
      {showTrial ? <>
        <Grid><NumberControl field={F.voiceDuration} /><NumberControl field={F.voiceBudget} /></Grid>
        <Notice><span key={tUi("durationAndBudgetApplyToTrialsStartedAfter")}>{tUi("durationAndBudgetApplyToTrialsStartedAfter")}</span></Notice>
      </> : null}
      <Grid><NumberControl field={F.agents} /><NumberControl field={F.concurrency} /></Grid>
      {!values.voice_included && !values.voice_trial_enabled ? <Notice><span key={tUi("thisPlanDoesNotIncludeVoiceOrOffer")}>{tUi("thisPlanDoesNotIncludeVoiceOrOffer")}</span></Notice> : null}
    </Card>
    <Card title={tUi("outboundCampaigns")}>
      <FormSwitch name="campaigns_enabled" label={L.campaigns_enabled} description={tUi("allowCreatingCampaignsWithinTheConfiguredCountLimit")} />
      <Notice><span key={tUi("startingCampaignCallsAlsoRequiresIncludedVoiceOr")}>{tUi("startingCampaignCallsAlsoRequiresIncludedVoiceOr")}</span></Notice>
      <NumberControl field={F.campaigns} />
      <FormSwitch name="campaigns_trial_enabled" label={L.campaigns_trial_enabled} description={tUi("whileASubscriptionIsInItsTrialTurning")} />
    </Card>
    <Card title={tUi("phoneNumbersRouting")}><Grid><NumberControl field={F.numbers} /><NumberControl field={F.flows} /></Grid></Card>
  </>
}

function Resources() {
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))

  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  return <>
    <Card title={tUi("usersCRM")}><Grid><NumberControl field={F.users} /><NumberControl field={F.crm} /></Grid></Card>
    <Card title={tUi("knowledgeBases")}>
      <NumberControl field={F.size} />
      <NumberControl field={F.knowledge} />
    </Card>
  </>
}

function Shopify() {
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))

  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  return <>
    <Card title={tUi("storeConnections")}><NumberControl field={F.stores} /><Notice><span key={tUi("connectingShopifyStoresIsIndependentOfThePayment")}>{tUi("connectingShopifyStoresIsIndependentOfThePayment")}</span></Notice></Card>
    <Card title={tUi("analyticsAccess")}>
      <NumberControl field={F.insights} />
      <Notice><span key={tUi("revenueAndOrderTotalsOrdersAndCatalogRemain")}>{tUi("revenueAndOrderTotalsOrdersAndCatalogRemain")}</span></Notice>
    </Card>
    <Card title={tUi("attributionChannels")}><NumberControl field={F.channels} /></Card>
  </>
}

function Lifecycle() {
  const F = getNUMERIC_FIELDS(useTranslations("PlanCatalog"))

  const tUi = useTranslations("Billing.BillingPlanEditorSections")

  return <Card title={tUi("workspaceLifecycle")}><NumberControl field={F.expiry} /><NumberControl field={F.grace} /></Card>
}

function visibilityOptions(system: boolean, status: string, tUi: ReturnType<typeof useTranslations<"Billing.BillingPlanEditorSections">>) {
  if (status === 'archived') return [{ value: 'archived', label: tUi("archived") }]
  const options = [{ value: 'internal', label: tUi("internalAdminAssignment") }, { value: 'published', label: tUi("publishedAvailableToCustomers") }]
  if (!system) options.unshift({ value: 'draft', label: tUi("draftNotAvailableYet") })
  return options
}
